import { 
  Sparkles,
  Users,
  Play,
  ArrowRight,
  Target,
  TrendingUp,
  Building2
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const steps = [
  {
    id: 1,
    title: 'Create Hiring Project',
    description: 'Set up your hiring project with job requirements, team members, and hiring goals in our comprehensive dashboard.',
    icon: Target,
    iconColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100/70 dark:bg-blue-900/20',
  },
  {
    id: 2,
    title: 'Upload & Analyze Resumes', 
    description: 'Upload candidate resumes and get instant AI-powered analysis with detailed scoring, skill matching, and experience verification.',
    icon: Sparkles,
    iconColor: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100/70 dark:bg-purple-900/20',
  },
  {
    id: 3,
    title: 'Review Market Intelligence',
    description: 'Access salary insights, talent trends, and competitive market data to make informed hiring decisions and set competitive offers.',
    icon: TrendingUp,
    iconColor: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100/70 dark:bg-green-900/20',
  },
  {
    id: 4,
    title: 'Collaborate & Decide',
    description: 'Share insights with your team, generate professional reports, create interview plans, and make data-driven hiring decisions.',
    icon: Users,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-100/70 dark:bg-indigo-900/20',
  },
  {
    id: 5,
    title: 'Track & Optimize',
    description: 'Monitor hiring performance, analyze team metrics, track ROI, and continuously optimize your recruitment process.',
    icon: Building2,
    iconColor: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100/70 dark:bg-orange-900/20',
  }
];

export function HowItWorks() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="relative bg-background py-20 sm:py-28 overflow-hidden" id="how-it-works">
       <div
         aria-hidden="true"
         className="absolute inset-x-0 top-0 h-[800px] -z-10 transform-gpu overflow-hidden blur-3xl"
        >
         <div
           className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary/20 to-secondary/20 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
           style={{
             clipPath:
               'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
           }}
         />
        </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         <div className="text-center mb-12 sm:mb-16">
           <h2 className={cn(
              "text-sm font-semibold tracking-wide uppercase mb-2",
              "text-primary"
          )}>
            Complete Hiring Platform
          </h2>
          <p className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground">
            From Project Creation to Perfect Hire
          </p>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground mx-auto">
            Our comprehensive platform guides you through every step of the hiring process with AI-powered insights and team collaboration
          </p>
        </div>

        {/* Video Preview */}
        <div className="relative max-w-4xl mx-auto mb-20">
          <div 
            className="aspect-video rounded-xl overflow-hidden border-2 border-border/50 shadow-xl bg-card/60 relative cursor-pointer group"
            onClick={() => setVideoOpen(true)}
          >
            <img 
              src="/images/Screenshot 2025-04-29 at 14.11.00.png" 
              alt="TopMatchTalent Demo" 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-all">
              <div className="bg-primary/90 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="h-6 w-6 sm:h-8 sm:w-8 text-white fill-white ml-1" />
              </div>
            </div>
            <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 bg-black/70 px-4 py-2 rounded-lg">
              <p className="text-white font-medium">Watch product demo</p>
            </div>
          </div>
        </div>

        <div className="relative">
          <div 
            className="absolute left-0 right-0 top-[40px] h-0.5 w-full max-w-4xl mx-auto"
            style={{ 
                background: 'repeating-linear-gradient(to right, hsl(var(--border)), hsl(var(--border)) 4px, transparent 4px, transparent 10px)',
                zIndex: 0 
            }}
            aria-hidden="true"
          />

          <div className="grid grid-cols-1 gap-y-16 sm:grid-cols-2 lg:grid-cols-5 sm:gap-x-8 relative z-10">
            {steps.map((step, idx) => (
              <div key={step.id} className="flex flex-col items-center text-center group relative">
                {idx < steps.length - 1 && (
                  <ArrowRight className="absolute right-0 top-10 h-6 w-6 text-muted-foreground/40 hidden lg:block -mr-10" />
                )}
                <div className={cn(
                    "flex h-20 w-20 items-center justify-center rounded-full border-4 border-background shadow-md mb-6",
                    step.bgColor,
                    "group-hover:scale-105 group-hover:shadow-lg transition-transform duration-300"
                )}>
                    <div className="relative">
                      <step.icon className={cn("h-8 w-8", step.iconColor)} aria-hidden="true" />
                      <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground h-5 w-5 flex items-center justify-center rounded-full text-xs font-bold">
                        {step.id}
                      </span>
                    </div>
                </div>
                
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                
                <p className="text-sm text-muted-foreground leading-relaxed px-2">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-20 flex justify-center">
          <Button size="lg" className="group">
            Start Free Trial
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>

      {/* Video Dialog */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="max-w-4xl p-1 sm:p-2">
          <DialogHeader className="sr-only">
            <DialogTitle>Product Demo Video</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            <video 
              className="w-full h-full" 
              src="/Videos/Demo for TOPMATCHTALENT.mp4" 
              title="TopMatchTalent Product Demo"
              controls
              autoPlay
              playsInline
              poster="/images/Screenshot 2025-04-29 at 14.11.00.png"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}